<div
  class="hx-table-grid__wrapper"
  role="table"
  [attr.aria-label]="ariaLabel()"
  [class]="cssClass()"
  [class.hx-table-grid__wrapper--no-outline]="!showRowOutline()">
  <!-- Label -->
  <hx-label
    [description]="label()"
    [required]="labelRequired()"
    [subtitle]="labelSubtitle()"
    [fontWeight]="labelFontWeight()"
    [fontSize]="labelFontSize()"
    [subtitleFontWeight]="subtitleFontWeight()"
    [subtitleFontSize]="subtitleFontSize()"
    [color]="labelColor()"
    [subtitleColor]="subtitleColor()"
    [tooltip]="tooltip()"
    [tooltipPosition]="tooltipPosition()"
    [tooltipColor]="tooltipColor()"
    class="hx-table-grid__label"></hx-label>

  <hx-table
    class="hx-table-grid__table"
    hxGridTabNav
    hxGridTabNavRowSelector="hx-table-header, hx-table-row"
    [hxGridTabNavDisabled]="!!disabled() || readonly()"
    [disabled]="!!disabled()"
    [enableRowHover]="enableRowHover()"
    [resizableColumns]="resizableColumns()"
    [maxHeight]="null"
    [footerFixed]="false"
    [readonly]="readonly()"
    [id]="tableId()"
    (columnsResized)="onColumnsResized($event)">
    @if (showHeader()) {
      <hx-table-header
        class="hx-table-grid__header"
        [class.hx-sticky-header]="stickyHeader()"
        [style.background-color]="rowBackgroundColor() || undefined">
        <ng-template #configureColumnsBtnTpl>
          <button
            type="button"
            #configureColumnsBtn
            class="hx-table-grid__configure-columns-btn"
            [class.is-configured]="hasCustomColumnConfig()"
            title="Configure columns"
            aria-label="Configure columns"
            (click)="toggleColumnConfig(configureColumnsBtn); $event.stopPropagation()">
            <hx-icon svgIcon="columns" size="14" />
          </button>
        </ng-template>
        @if (showSerialNumber()) {
          <hx-header-cell
            class="hx-table-grid__serial-cell"
            [width]="effectiveSerialNumberWidth()"
            [tooltip]="'#'"
            [align]="'center'">
            <div class="hx-table-grid__header-cell-content">
              @if (showConfigureColumns() && configureColumnsHost() === 'serial') {
                <ng-container *ngTemplateOutlet="configureColumnsBtnTpl"></ng-container>
              }
              <span>#</span>
            </div>
          </hx-header-cell>
        }

        @if (showRowActions()) {
          <hx-header-cell [width]="effectiveRowActionsWidth()" [tooltip]="'Actions'">
            <div class="hx-table-grid__header-cell-content">
              @if (showConfigureColumns() && configureColumnsHost() === 'rowActions') {
                <ng-container *ngTemplateOutlet="configureColumnsBtnTpl"></ng-container>
              }
              <ng-content select="[slot=row-actions-header]"></ng-content>
            </div>
          </hx-header-cell>
        }

        <hx-header-cell [width]="effectiveQuestionLabelWidth()" [tooltip]="questionLabel()">
          <div class="hx-table-grid__header-cell-content">
            @if (showConfigureColumns() && configureColumnsHost() === 'questionLabel') {
              <ng-container *ngTemplateOutlet="configureColumnsBtnTpl"></ng-container>
            }
            @if (questionLabelTemplate()) {
              <ng-container
                *ngTemplateOutlet="questionLabelTemplate()!; context: { $implicit: questionLabel() }"></ng-container>
            } @else {
              {{ questionLabel() }}
            }
          </div>
        </hx-header-cell>

        @for (col of visibleColumns(); track col.id; let i = $index) {
          <hx-header-cell
            [width]="getColumnWidth(col)"
            [tooltip]="col.label"
            [sticky]="col.sticky || false"
            [class]="'text-' + headerAlignment()">
            <div style="display: flex; flex-direction: column; align-items: center; gap: 4px">
              @if (columnTemplate()) {
                <ng-container
                  *ngTemplateOutlet="columnTemplate()!; context: { $implicit: col, index: i }"></ng-container>
              } @else {
                <span style="width: 100%; white-space: nowrap; text-overflow: ellipsis; overflow: hidden">
                  {{ col.label }}
                </span>
              }
              @if (shouldShowSelectAllForColumn(col.id)) {
                <div class="hx-table-grid__select-all-label">
                  <hx-radio
                    [id]="getSelectAllRadioId(col)"
                    [disabled]="disabled() || readonly()"
                    [name]="getSelectAllRadioName(col)"
                    [value]="inputType() === 'checkbox' ? true : col.value || col.id"
                    [(ngModel)]="selectAllStatesPerColumn()[getSelectAllStateKey(col)]"
                    (change)="selectAllForColumn(inputType() === 'checkbox' ? col.id : col.value || col.id)">
                    All
                  </hx-radio>
                </div>
              }
            </div>
          </hx-header-cell>
        }
      </hx-table-header>
    }
    @for (section of sections(); track section.sectionLabel; let sectionIdx = $index) {
      <!-- Section Label using hx-table-section -->
      <hx-table-section
        [label]="sectionHasVisibleRows(section) ? section.sectionLabel : null"
        [labelTemplate]="sectionLabelTemplate() ?? null"
        [sectionIndex]="sectionIdx"
        [labelColor]="labelColor()"
        [id]="getDropListId(sectionIdx)"
        cdkDropList
        [cdkDropListData]="section.rows"
        [cdkDropListConnectedTo]="getConnectedDropLists(sectionIdx)"
        (cdkDropListDropped)="onRowDrop($event)"
        class="hx-table-grid__section-drop-zone">
        @for (row of section.rows; track row.linkId; let i = $index) {
          <hx-table-row
            *ngIf="
              row.showDefault == undefined || row.showDefault || !conditionalScore() || shouldShowConditionalRows()
            "
            [class.row-error]="shouldShowError(row)"
            [style.background-color]="getRowBackgroundColor(i)"
            cdkDrag
            [cdkDragData]="row"
            [cdkDragDisabled]="!enableRowReordering()"
            [class.cdk-drag-disabled]="!enableRowReordering()">
            @if (showSerialNumber()) {
              @if (enableSectionMode()) {
                <hx-table-cell class="hx-table-grid__serial-cell" [align]="'center'">
                  {{ sectionIdx + 1 }}.{{ getSecondaryIndex(i) }}
                </hx-table-cell>
              } @else {
                <hx-table-cell class="hx-table-grid__serial-cell" [align]="'center'">{{ i + 1 }}</hx-table-cell>
              }
            }

            @if (showRowActions()) {
              <hx-table-cell>
                @if (rowActionsTemplate()) {
                  <ng-container
                    *ngTemplateOutlet="
                      rowActionsTemplate()!;
                      context: { $implicit: row, required: isRowRequired(row) }
                    "></ng-container>
                }
              </hx-table-cell>
            }

            <hx-table-cell>
              @if (rowLabelTemplate()) {
                <ng-container
                  *ngTemplateOutlet="
                    rowLabelTemplate()!;
                    context: { $implicit: row, required: isRowRequired(row) }
                  "></ng-container>
              } @else {
                <span class="hx-table-grid__label">
                  {{ row.label }}
                  @if (isRowRequired(row)) {
                    <span class="hx-table-grid__required-star">*</span>
                  }
                </span>
              }
            </hx-table-cell>

            @for (col of visibleColumns(); track col.id) {
              <hx-table-cell>
                @if (inputType() === 'radio') {
                  <div class="hx-table-grid__cell-control" [class]="'text-' + bodyAlignment()">
                    <hx-radio
                      [disabled]="disabled() || readonly()"
                      [name]="row.linkId"
                      [value]="col.value || col.id"
                      [checkedOverride]="isRadioSelected(row.linkId, col)"
                      (click)="onRadioSelect(row.linkId, col.value || col.id, $event)">
                      {{ getCellLabel(row, col) }}
                    </hx-radio>
                  </div>
                }

                @if (inputType() === 'checkbox') {
                  <div class="hx-table-grid__cell-control" [class]="'text-' + bodyAlignment()">
                    <hx-checkbox-with-label
                      [options]="[{ label: getCellLabel(row, col), value: col.id }]"
                      [disabled]="!!disabled() || readonly()"
                      (valueChange)="
                        onCheckboxChange(row.linkId, col.id, $any($event)?.length > 0)
                      "></hx-checkbox-with-label>
                  </div>
                }

                @if (inputType() === 'text') {
                  <div class="hx-table-grid__cell-control" [class]="'text-' + bodyAlignment()">
                    <hx-input
                      [type]="col.textInputType || 'text'"
                      [readonly]="readonly()"
                      [attr.maxlength]="col.characterLimit"
                      [value]="getTextValue(row.linkId, col.id)"
                      (input)="onTextInput(row.linkId, col.id, $event)"
                      [disabled]="disabledState()"
                      [pattern]="
                        col.pattern ? (typeof col.pattern === 'string' ? col.pattern : col.pattern.source) : undefined
                      "
                      [patternErrorMessage]="col.patternErrorMessage || 'Input does not match required format.'"
                      class="hx-table-grid__input" />
                  </div>
                }
              </hx-table-cell>
            }
          </hx-table-row>
        }
      </hx-table-section>
    }

    @if (showTotalCount()) {
      <hx-table-footer [style.background-color]="totalBackgroundColor() || 'var(--color-primary-light)'">
        <hx-label
          [description]="'Score: ' + totalScore()"
          [bold]="totalBold()"
          [color]="totalFontColor() || 'var(--color-primary)'"
          [contentJustification]="totalPosition()"></hx-label>
      </hx-table-footer>
    }
  </hx-table>

  @if ((pageSize() ?? initialPageSize()) < effectiveTotalRecordCount()) {
    <div class="hx-table-grid__footer">
      @if ((pageSize() ?? initialPageSize()) < effectiveTotalRecordCount()) {
        <hx-pagination
          [totalRecordCount]="effectiveTotalRecordCount()"
          [currentPage]="currentPage()"
          [pageSize]="pageSize()"
          [pageSizeOptions]="pageSizeOptions()"
          [initialPageSize]="initialPageSize()"
          [rowsPerPageLabel]="rowsPerPageLabel()"
          [gotoPageLabel]="gotoPageLabel()"
          [showGotoPage]="showGotoPage()"
          [gotoPageErrorMessage]="gotoPageErrorMessage()"
          [disabled]="!!disabled()"
          (pageNumberChange)="onPageChange($event)"
          (pageSizeChangeEvent)="onPageSizeChange($any($event).pageSize)"></hx-pagination>
      }
    </div>
  }
  @if (invalidValidationModeWarning()) {
    <hx-alert type="warning" style="margin-top: 16px">
      {{ invalidValidationModeWarning() }}
    </hx-alert>
  }
  @if (validationState().tableErrorMessage && isTouched()) {
    <div class="hx-table-grid__error-footer">
      <span class="hx-table-grid__error-icon">⚠</span>
      {{ validationState().tableErrorMessage }}
    </div>
  }

  @if (showColumnConfig()) {
    <div
      class="hx-table-grid__column-config-popover"
      [style.top.px]="columnConfigPosition().top"
      [style.left.px]="columnConfigPosition().left"
      (click)="$event.stopPropagation()">
      <div class="hx-table-grid__column-config-header">
        <span>Configure Columns</span>
        <button type="button" class="hx-table-grid__column-config-reset" (click)="resetColumnConfig()">Reset</button>
      </div>
      @for (col of columns(); track col.id) {
        <label class="hx-table-grid__column-config-option">
          <input type="checkbox" [checked]="!isColumnHidden(col.id)" (change)="toggleColumnVisibility(col.id)" />
          <span>{{ col.label }}</span>
        </label>
      }
    </div>
  }
</div>
